/**
 * GA4 Event Tracking Utilities
 * Handles GA4 event tracking via GTM and direct gtag
 */

import { pushToDataLayer, isDataLayerAvailable } from './gtm';
import type { GA4EventParams } from './types';

/**
 * Check if gtag is available
 */
export const isGtagAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  return typeof window.gtag === 'function';
};

/**
 * Track a GA4 event
 * Works via both GTM and direct gtag if available
 * 
 * @param eventName - GA4 event name (e.g., 'purchase', 'add_to_cart')
 * @param params - Event parameters
 * 
 * @example
 * trackEvent('purchase', {
 *   transaction_id: '123',
 *   value: 99.99,
 *   currency: 'INR'
 * });
 */
export const trackEvent = (
  eventName: string,
  params: Record<string, any> = {}
): boolean => {
  if (typeof window === 'undefined') return false;

  const success: boolean[] = [];

  // Method 1: Via GTM dataLayer (primary, works in most cases)
  if (isDataLayerAvailable()) {
    success.push(
      pushToDataLayer(eventName, {
        ecommerce: params.ecommerce,
        ...params,
      })
    );
  }

  // Method 2: Via direct gtag (secondary, if gtag function available)
  if (isGtagAvailable()) {
    try {
      window.gtag?.('event', eventName, params);
      success.push(true);
    } catch (error) {
      console.error('[GA4] Error calling gtag:', error);
      success.push(false);
    }
  }

  // If neither worked, warn developer
  if (success.length === 0) {
    console.warn(
      `[GA4] Warning: Event '${eventName}' could not be tracked. No tracking available.`
    );
    return false;
  }

  if (process.env.NEXT_PUBLIC_DEBUG_TRACKING === 'true') {
    console.log(`[GA4] Event '${eventName}' tracked:`, params);
  }

  return true;
};

/**
 * Track a GA4 event with ecommerce data
 * Specifically formatted for ecommerce events
 */
export const trackEcommerceEvent = (
  eventName: string,
  ecommerceData: Record<string, any>
): boolean => {
  return trackEvent(eventName, {
    ecommerce: ecommerceData,
    ...ecommerceData,
  });
};

/**
 * Track a GA4 conversion event
 * For Google Ads conversion tracking
 * 
 * @param conversionName - Conversion name (e.g., 'purchase')
 * @param conversionValue - Conversion value (optional)
 * @param conversionCurrency - Conversion currency (optional)
 */
export const trackConversion = (
  conversionName: string,
  conversionValue?: number,
  conversionCurrency: string = 'INR'
): boolean => {
  const params: GA4EventParams = {
    event_category: 'conversion',
    event_label: conversionName,
  };

  if (conversionValue !== undefined) {
    params.value = conversionValue;
    params.currency = conversionCurrency;
  }

  return trackEvent(conversionName, params);
};

/**
 * Track page view
 * Useful for SPA navigation tracking
 */
export const trackPageView = (
  pagePath: string,
  pageTitle?: string
): boolean => {
  return trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: typeof window !== 'undefined' ? window.location.href : '',
  });
};

/**
 * Track custom event
 * Generic event tracking for non-standard events
 */
export const trackCustomEvent = (
  eventName: string,
  eventCategory: string,
  eventLabel?: string,
  eventValue?: number
): boolean => {
  return trackEvent(eventName, {
    event_category: eventCategory,
    event_label: eventLabel,
    value: eventValue,
  });
};

/**
 * Set GA4 user ID
 * Must be called after user auth
 */
export const setUserId = (userId: string): boolean => {
  try {
    if (isGtagAvailable()) {
      window.gtag?.('config', 'GA_MEASUREMENT_ID', {
        user_id: userId,
      });
    }

    pushToDataLayer('set', {
      user_id: userId,
    });

    if (process.env.NEXT_PUBLIC_DEBUG_TRACKING === 'true') {
      console.log('[GA4] User ID set:', userId);
    }

    return true;
  } catch (error) {
    console.error('[GA4] Error setting user ID:', error);
    return false;
  }
};

/**
 * Set GA4 user properties
 * For custom user attributes
 */
export const setUserProperties = (
  properties: Record<string, string | number | boolean>
): boolean => {
  try {
    if (isGtagAvailable()) {
      window.gtag?.('set', {
        user_properties: properties,
      });
    }

    return pushToDataLayer('set', {
      user_properties: properties,
    });
  } catch (error) {
    console.error('[GA4] Error setting user properties:', error);
    return false;
  }
};

/**
 * Initialize GA4 debugging
 * Enables GA4 console logging
 */
export const initializeGA4Debug = (): void => {
  if (typeof window === 'undefined') return;

  // For Google Analytics 4, you can enable debug mode via gtag config
  if (isGtagAvailable()) {
    window.gtag?.('config', 'G_MEASUREMENT_ID', {
      debug_mode: true,
    });
  }

  if (process.env.NEXT_PUBLIC_DEBUG_TRACKING === 'true') {
    console.log('[GA4] Debug mode enabled. Open Google Analytics 4 Realtime report to see events.');
  }
};
